// src/pages/MarketplaceIntegration/MarketplaceConnection.jsx

import React, { useState } from 'react';

const MarketplaceConnection = ({ marketplaces, onAdd, onUpdate, onDelete }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    storeName: '',
    apiKey: '',
    apiSecret: '',
    apiType: '',
    supplierId: '', // Add supplierId field
    isActive: true
  });
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);

  const marketplaceOptions = [
    { id: 'trendyol', name: 'Trendyol' },
    { id: 'hepsiburada', name: 'Hepsiburada' },
    { id: 'amazon', name: 'Amazon' },
    { id: 'n11', name: 'n11' },
    { id: 'etsy', name: 'Etsy' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate supplier ID for Trendyol
    if (formData.name.toLowerCase() === 'trendyol' && !formData.supplierId) {
      alert('Trendyol için Tedarikçi ID gereklidir.');
      return;
    }
    
    try {
      if (editingId) {
        await onUpdate(editingId, formData);
        setEditingId(null);
      } else {
        await onAdd(formData);
      }
      
      // Reset form
      setFormData({
        name: '',
        storeName: '',
        apiKey: '',
        apiSecret: '',
        apiType: '',
        isActive: true
      });
      setShowForm(false);
    } catch (error) {
      console.error('Error saving marketplace:', error);
      alert('Bağlantı kaydedilirken bir hata oluştu.');
    }
  };

  const handleEdit = (marketplace) => {
    setFormData({
      name: marketplace.name,
      storeName: marketplace.storeName || '',
      apiKey: marketplace.apiKey || '',
      apiSecret: marketplace.apiSecret || '',
      apiType: marketplace.apiType || '',
      isActive: marketplace.isActive !== undefined ? marketplace.isActive : true
    });
    setEditingId(marketplace.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu pazaryeri bağlantısını silmek istediğinizden emin misiniz?')) {
      try {
        await onDelete(id);
      } catch (error) {
        console.error('Error deleting marketplace:', error);
        alert('Bağlantı silinirken bir hata oluştu.');
      }
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    
    // Simulate API test
    setTimeout(() => {
      // In a real implementation, this would actually test the API connection
      const isSuccess = Math.random() > 0.3; // 70% success rate for demo
      setTestResult({
        success: isSuccess,
        message: isSuccess 
          ? 'Bağlantı başarılı!' 
          : 'Bağlantı başarısız. Lütfen bilgilerinizi kontrol edin.'
      });
      setIsTesting(false);
    }, 1500);
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      storeName: '',
      apiKey: '',
      apiSecret: '',
      apiType: '',
      isActive: true
    });
    setEditingId(null);
    setShowForm(false);
    setTestResult(null);
  };

  return (
    <div className="marketplace-connection">
      <div className="connection-header">
        <h2>Pazaryeri Bağlantıları</h2>
        <button 
          className="add-button"
          onClick={() => setShowForm(true)}
        >
          + Yeni Bağlantı Ekle
        </button>
      </div>

      {showForm ? (
        <div className="connection-form">
          <h3>{editingId ? 'Bağlantıyı Düzenle' : 'Yeni Bağlantı Ekle'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Pazaryeri</label>
              <select
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              >
                <option value="">Pazaryeri Seçin</option>
                {marketplaceOptions.map(option => (
                  <option key={option.id} value={option.name}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="storeName">Mağaza Adı</label>
              <input
                type="text"
                id="storeName"
                name="storeName"
                value={formData.storeName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="apiKey">API Anahtarı</label>
              <input
                type="password"
                id="apiKey"
                name="apiKey"
                value={formData.apiKey}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="apiSecret">API Gizli Anahtarı</label>
              <input
                type="password"
                id="apiSecret"
                name="apiSecret"
                value={formData.apiSecret}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* Add supplier ID field */}
            <div className="form-group">
              <label htmlFor="supplierId">Tedarikçi ID (Sadece Trendyol için)</label>
              <input
                type="text"
                id="supplierId"
                name="supplierId"
                value={formData.supplierId}
                onChange={handleInputChange}
                placeholder="Trendyol için gerekli"
              />
            </div>

            <div className="form-group">
              <label htmlFor="apiType">API Türü</label>
              <select
                id="apiType"
                name="apiType"
                value={formData.apiType}
                onChange={handleInputChange}
                required
              >
                <option value="">API Türü Seçin</option>
                <option value="rest">REST API</option>
                <option value="soap">SOAP API</option>
                <option value="graphql">GraphQL</option>
              </select>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                />
                Bağlantıyı etkinleştir
              </label>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="test-button"
                onClick={handleTestConnection}
                disabled={isTesting || !formData.apiKey || !formData.apiSecret || 
                  (formData.name.toLowerCase() === 'trendyol' && !formData.supplierId)}
              >
                {isTesting ? 'Test Ediliyor...' : 'Bağlantıyı Test Et'}
              </button>
              
              {testResult && (
                <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                  {testResult.message}
                </div>
              )}
            </div>

            <div className="form-buttons">
              <button type="button" onClick={handleCancel} className="cancel-button">
                İptal
              </button>
              <button type="submit" className="save-button">
                {editingId ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="marketplace-list">
        {marketplaces.length === 0 ? (
          <div className="empty-state">
            <p>Henüz bir pazaryeri bağlantısı eklenmemiş.</p>
          </div>
        ) : (
          <table className="marketplace-table">
            <thead>
              <tr>
                <th>Pazaryeri</th>
                <th>Mağaza Adı</th>
                <th>API Türü</th>
                <th>Durum</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {marketplaces.map(marketplace => (
                <tr key={marketplace.id}>
                  <td>
                    <div className="marketplace-name">
                      {marketplace.name}
                    </div>
                  </td>
                  <td>{marketplace.storeName || '-'}</td>
                  <td>{marketplace.apiType || '-'}</td>
                  <td>
                    <span className={`status ${marketplace.isActive ? 'active' : 'inactive'}`}>
                      {marketplace.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="edit-button"
                        onClick={() => handleEdit(marketplace)}
                      >
                        Düzenle
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => handleDelete(marketplace.id)}
                      >
                        Sil
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MarketplaceConnection;